import { useState, useEffect } from 'react'
import { Snowflake } from 'lucide-react'
import { ModalOverlay } from './ModalOverlay'

const F = 'Roboto, sans-serif'

interface Props {
  open: boolean
  onClose: () => void
  initialSeconds?: number
  /** Current streak in days — interpolated into the warning text. */
  streakDays?: number
}

function pluralizeDays(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'день'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'дня'
  return 'дней'
}

function formatTime(s: number) {
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
}

export function FreezeActiveModal({ open, onClose, initialSeconds = 85512, streakDays = 0 }: Props) {
  const [seconds, setSeconds] = useState(initialSeconds)

  useEffect(() => {
    if (!open) return
    setSeconds(initialSeconds)
    const id = setInterval(() => setSeconds((p) => Math.max(0, p - 1)), 1000)
    return () => clearInterval(id)
  }, [open, initialSeconds])

  const pct = seconds / 86400

  return (
    <ModalOverlay open={open} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', fontFamily: F, gap: 16 }}>
        <div style={{ width: 80, height: 80, borderRadius: 40, background: 'radial-gradient(circle, rgba(74,159,229,0.2), transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', filter: 'drop-shadow(0 0 12px rgba(74,159,229,0.5))' }}>
          <Snowflake size={48} color="#4A9FE5" />
        </div>
        <h2 style={{ fontSize: 20, fontWeight: 600, color: '#1C2D41', margin: 0, textAlign: 'center' }}>У тебя есть 24 часа!</h2>
        <div style={{ position: 'relative', width: 120, height: 120 }}>
          <svg width={120} height={120} viewBox="0 0 120 120">
            <circle cx={60} cy={60} r={52} fill="none" stroke="rgba(26,58,92,0.08)" strokeWidth={6} />
            <circle cx={60} cy={60} r={52} fill="none" stroke="url(#freezeGrad)" strokeWidth={6} strokeLinecap="round" strokeDasharray={2 * Math.PI * 52} strokeDashoffset={2 * Math.PI * 52 * (1 - pct)} transform="rotate(-90 60 60)" style={{ transition: 'stroke-dashoffset 1s linear' }} />
            <defs>
              <linearGradient id="freezeGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#1A3A5C" />
                <stop offset="100%" stopColor="#4A6CF7" />
              </linearGradient>
            </defs>
          </svg>
          <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, color: '#1C2D41' }}>{formatTime(seconds)}</span>
        </div>
        <p style={{ fontSize: 15, color: '#6B7B8D', margin: 0, textAlign: 'center', lineHeight: 1.5 }}>
          ❄️ Заморозка <b>активна</b> — мы не снимаем жизни, пока действует.
          Выполни любое действие, чтобы остаться в режиме.
        </p>
        <p style={{ fontSize: 12, color: '#9AA5B4', margin: 0, textAlign: 'center', lineHeight: 1.5, padding: '0 12px' }}>
          Если за 24 часа ты не отметишь активность — потеряешь 1 жизнь. Когда сгорят все 3 жизни, текущий стаж <b>{streakDays} {pluralizeDays(streakDays)}</b> в режиме обнулится. Стартовая заморозка одна на программу.
        </p>
        <button onClick={onClose} style={{ width: '100%', height: 52, borderRadius: 16, border: 'none', background: 'linear-gradient(135deg, #1A3A5C, #4A6CF7)', color: 'white', fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: F }}>
          Выполнить задание сейчас
        </button>
      </div>
    </ModalOverlay>
  )
}
